import axios, { AxiosRequestConfig } from 'axios';
import {
  axiosInventoryInstance,
  axiosSmartCoolersInstance,
} from '@/config/apiConfig';
import { customUUIDv4 } from '@/utils/generic';
import _ from 'lodash';
import {
  ALL_MARKET,
  ALL_SUB_APPLICATION,
  DOWNLOAD_ERROR_MESSAGE,
} from '../global/global.constant';
import { QUERY_PARAMS_KEYS } from './downloadReport.const';

// For exporting the csv or pdf
// --- Types & Interfaces ---

interface DownloadParams {
  url: string;
  fileName?: string;
  operatorId?: string;
  marketId?: string;
  deviceId?: string;
  order?: string;
  orderBy?: string;
  limit?: number;
  dateFilterBy?: string;
  timeZone?: string;
  startDate?: string;
  endDate?: string;
  inactive?: boolean;
  subAppId?: string;
  exportPayload: {
    documentFormat: string;
    page?: number;
    limit?: number;
    fields?: string[];
  };
  sourceOrderId?: string;
  [key: string]: any; // for remainingParams
}

export enum ERequestMethods {
  GET = 'get',
  POST = 'post',
}

interface InternalConfig {
  method: ERequestMethods;
  url: string;
  type: string;
  fileName?: string;
  payload: Record<string, any>;
}

// --- Helper Functions ---

const getAccessToken = () => localStorage.getItem('access_token');

const preparePayload = (
  params: Partial<DownloadParams>,
  type: string,
): Record<string, any> => {
  const {
    operatorId,
    marketId,
    deviceId,
    order,
    orderBy,
    limit,
    dateFilterBy,
    timeZone,
    startDate,
    endDate,
    inactive,
    subAppId,
    exportPayload,
    fileName,
    sourceOrderId,
    sourceProductId,
    name,
    productCode,
    includeInactive,
    deviceIdentifier,
    deviceType,
    selectiveFieldExport,
    barcode,
    ...remainingParams
  } = params;
  const rawBody = {
    orderBy: orderBy || 'createdOn',
    order: order || 'desc',
    format: exportPayload?.documentFormat?.toLowerCase() || type,
    page: exportPayload?.page,
    limit: exportPayload?.limit,
    ...(operatorId && { operatorId }),
    ...(!_.isEmpty(sourceOrderId) && { sourceOrderId }),
    ...(marketId !== ALL_MARKET.id && { marketId }),
    ...(deviceId && { deviceId }),
    ...(sourceProductId && { sourceProductId }),
    ...(productCode && { productCode }),
    ...(name && { name }),
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
    ...(dateFilterBy && { dateFilterBy }),
    ...(timeZone && { timeZone }),
    ...(inactive !== undefined && { inactive }),
    ...(includeInactive !== undefined && { includeInactive }),
    ...(subAppId !== ALL_SUB_APPLICATION.id && { subAppId }),
    ...(deviceIdentifier && { deviceIdentifier }),
    ...(deviceType && { deviceType }),
    ...(barcode && { barcode }),
    ...remainingParams,
    type: exportPayload?.documentFormat?.toLowerCase() || type,
    ...(selectiveFieldExport &&
      exportPayload?.fields?.length && { fields: exportPayload.fields }),
  };
  // Filter out undefined/null
  return Object.fromEntries(
    Object.entries(rawBody).filter(([_, v]) => v !== undefined && v !== null),
  );
};

const splitQueryAndBodyParams = (payload: any) => {
  const queryParams: any = {};
  const bodyPayload: any = {};

  Object.keys(payload || {}).forEach((key) => {
    const value = payload[key];

    if (QUERY_PARAMS_KEYS.includes(key)) {
      if (
        value !== undefined &&
        value !== null &&
        !(typeof value === 'string' && value.trim() === '')
      ) {
        queryParams[key] = value;
      }
    } else {
      bodyPayload[key] = value;
    }
  });

  return { queryParams, bodyPayload };
};

const executeBlobDownload = async ({
  method,
  url,
  type,
  fileName,
  payload,
}: InternalConfig) => {
  const downloadFileName = fileName
    ? `${fileName}-${customUUIDv4()}.${type}`
    : `file.${type}`;

  const { queryParams, bodyPayload } =
    method === ERequestMethods.POST
      ? splitQueryAndBodyParams(payload)
      : { queryParams: payload, bodyPayload: undefined };

  const config: AxiosRequestConfig = {
    method,
    url,
    headers: {
      Accept: `application/${type}`,
      Authorization: `Bearer ${getAccessToken()}`,
    },
    responseType: 'blob',
    ...(method === ERequestMethods.GET
      ? { params: queryParams }
      : {
        params: queryParams,
        data: bodyPayload,
      }),
  };

  try {
    const response = await axiosInventoryInstance(config);

    const blobUrl = window.URL.createObjectURL(
      new Blob([response?.data], { type: `application/${type}` }),
    );
    const link = document?.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', downloadFileName);
    document?.body?.appendChild(link);
    link.click();
    document?.body?.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error: any) {
    console.error(`Error downloading ${type}:`, error);
  }
};

export const fetchFileDownload = (params: DownloadParams) => {
  const { url, fileName, exportPayload } = params;
  const exportType = exportPayload?.documentFormat?.toLowerCase();
  const payload = preparePayload(
    params,
    exportPayload?.documentFormat?.toLowerCase(),
  );
  return executeBlobDownload({
    method: ERequestMethods.GET,
    url,
    type: exportType,
    fileName,
    payload,
  });
};

export const postFetchFileDownload = (params: DownloadParams) => {
  const { url, fileName, exportPayload } = params;
  const exportType = exportPayload?.documentFormat?.toLowerCase();
  const payload = preparePayload(
    params,
    exportPayload?.documentFormat?.toLowerCase(),
  );
  return executeBlobDownload({
    method: ERequestMethods.POST,
    url,
    type: exportType,
    fileName,
    payload,
  });
};

/**
 * Calls the SmartCoolers API which returns a pre-signed download URL,
 * then triggers the browser download via an anchor click.
 */
export const directFileDownload = async ({
  url,
}: {
  url: string;
}): Promise<void> => {
  try {
    const response = await axiosSmartCoolersInstance({
      method: ERequestMethods.GET,
      url,
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    const responseData = response?.data;
    if (!responseData?.success) {
      throw new Error(
        responseData?.message || DOWNLOAD_ERROR_MESSAGE.FILE_DOWNLOAD_FAILED,
      );
    }
    const downloadUrl = responseData?.data?.downloadUrl;
    if (!downloadUrl) {
      throw new Error(DOWNLOAD_ERROR_MESSAGE.FILE_DOWNLOAD_FAILED);
    }
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error: any) {
    console.error(DOWNLOAD_ERROR_MESSAGE.FILE_DOWNLOAD_FAILED, error);
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      throw new Error(
        (status && DOWNLOAD_ERROR_MESSAGE[status]) ||
        error.response?.data?.message ||
        DOWNLOAD_ERROR_MESSAGE.DEFAULT,
      );
    }
    throw new Error(DOWNLOAD_ERROR_MESSAGE.UNEXPECTED);
  }
};
